/**
 * DarkConfirmationCard Component
 * 
 * Thank you message with waitlist position display.
 * Dark themed to match landing2.
 */

import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const DarkConfirmationCard = ({ submissionData, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Calculate approximate waitlist position from submission ID
  // Wrap in useMemo so it stays stable across component re-renders!
  const waitlistPosition = useMemo(() => {
    return submissionData?.data?.queue_position || submissionData?.data?.id || submissionData?.id || Math.floor(Math.random() * 50) + 1;
  }, [submissionData]);

  const emailDisplay = useMemo(() => {
    return submissionData?.data?.email || submissionData?.email || 'you';
  }, [submissionData]);

  const referenceId = useMemo(() => {
    return submissionData?.data?.id || submissionData?.id;
  }, [submissionData]);

  const iconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: { 
      scale: 1, 
      rotate: 0,
      transition: { 
        type: 'spring', 
        damping: 12, 
        stiffness: 200,
        delay: 0.2 
      }
    },
  };

  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: 0.4 + i * 0.1, duration: 0.5 },
    }),
  };

  return (
    <div className="dark-confirmation">
      <div className="dark-confirmation__card">
        {/* Success Icon */}
        <motion.div 
          className="dark-confirmation__icon"
          variants={iconVariants}
          initial="hidden"
          animate="visible"
        >
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </motion.div>

        {/* Title */}
        <motion.h2 
          className="dark-confirmation__title"
          custom={0}
          variants={textVariants}
          initial="hidden"
          animate="visible"
        >
          You're on the list!
        </motion.h2>

        {/* Waitlist Position */}
        <motion.div 
          className="dark-confirmation__position"
          custom={1}
          variants={textVariants}
          initial="hidden"
          animate="visible"
        >
          <span className="dark-confirmation__position-label">Your position</span>
          <span className="dark-confirmation__position-number">#{waitlistPosition}</span>
          <span className="dark-confirmation__position-context">of the founding cohort</span>
        </motion.div>

        {/* Message */}
        <motion.p 
          className="dark-confirmation__message"
          custom={2}
          variants={textVariants}
          initial="hidden"
          animate="visible"
        >
          {user
            ? "We've linked your submission to your account. You'll be among the first to know when we're ready to onboard."
            : `We'll reach out to ${emailDisplay} with updates. Creating an account can increase your priority for early access.`
          }
        </motion.p>

        {/* Reference ID */}
        {referenceId && (
          <motion.p
            className="dark-confirmation__reference"
            custom={3}
            variants={textVariants}
            initial="hidden"
            animate="visible"
          >
            Reference: #EA-{referenceId}
          </motion.p>
        )}

        {/* Next Steps */}
        <motion.div
          className="dark-confirmation__next"
          custom={4}
          variants={textVariants}
          initial="hidden"
          animate="visible"
        >
          <h3 className="dark-confirmation__next-title">What happens next?</h3>
          <ul className="dark-confirmation__next-list">
            <li>
              <span className="dark-confirmation__check">✓</span>
              We'll review your profile for the pilot cohort
            </li>
            <li>
              <span className="dark-confirmation__check">✓</span>
              Selected tutors will receive onboarding invitations
            </li>
            <li>
              <span className="dark-confirmation__check">✓</span>
              Pilot launch: January 2026
            </li>
          </ul>
        </motion.div>

        {/* Actions */}
        <motion.div
          className="dark-confirmation__actions"
          custom={5}
          variants={textVariants}
          initial="hidden"
          animate="visible"
        >
          <button
            className="dark-confirmation__btn dark-confirmation__btn--primary"
            onClick={() => {
              if (onClose) {
                onClose();
              } else if (window.location.pathname === '/') {
                if (typeof window !== 'undefined') window.location.reload();
              } else {
                navigate('/');
              }
            }}
          >
            Back to Homepage
          </button>
          {!user && (
            <button
              className="dark-confirmation__btn dark-confirmation__btn--ghost"
              onClick={() => {
                if (onClose) onClose();
                navigate('/login');
              }}
            >
              Create Account
            </button>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default DarkConfirmationCard;
